import { formatMoney } from '../lib/payments';
import { FREQUENCY_LABELS, disclosureLabel } from '../lib/paymentsUi';
import { ui, cn } from '../lib/ui';

function Row({ label, cents, strong = false, muted = false, rule = false }) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-4 py-1',
        rule && 'mt-1 border-t border-z-line pt-2',
        strong && 'font-semibold text-dcon-ink',
        muted && ui.muted
      )}
    >
      <dt>{label}</dt>
      <dd className="font-mono text-data">{formatMoney(cents)}</dd>
    </div>
  );
}

/**
 * Every line the donor pays, the total, and where each part of it goes
 * (docs/Decisions.md D44, D60): which fees are the platform's (the platform
 * fee and Stripe's processing) and which are the organization's (its own fee
 * for platform and processing costs, and the rest for its work). Design
 * rules: money carries its currency and every fee is stated before the
 * person pays.
 *
 * @param {object} props.breakdown - computeDonationBreakdown() output
 * @param {string} [props.frequency] - one_time | month | year
 */
export default function DonationBreakdown({
  breakdown,
  frequency = 'one_time',
  title = 'Summary',
}) {
  if (!breakdown) return null;
  const per = frequency !== 'one_time' ? `, ${FREQUENCY_LABELS[frequency].toLowerCase()}` : '';
  return (
    <div className={cn(ui.cardFlat, 'p-4!')} data-testid="donation-breakdown">
      <h3 className={ui.h3}>{title}</h3>
      <dl className="text-body-s text-dcon-ink-body">
        {breakdown.lineItems.map(item => (
          <Row key={item.key} label={item.label} cents={item.amountCents} />
        ))}
        <Row label={`Total charged${per}`} cents={breakdown.totalCents} strong rule />
      </dl>
      {Array.isArray(breakdown.disclosure) && breakdown.disclosure.length ? (
        <>
          <h4 className="mt-3 mb-1 text-body-s font-semibold text-dcon-ink">Where it goes</h4>
          <dl className="text-body-s text-dcon-ink-body" data-testid="donation-disclosure">
            {breakdown.disclosure.map(line => (
              <Row
                key={line.key}
                label={disclosureLabel(line)}
                cents={line.amountCents}
                muted={line.recipient !== 'organization'}
                strong={line.key === 'org_work'}
              />
            ))}
          </dl>
        </>
      ) : null}
    </div>
  );
}
